import { intlLocale } from './clockChange.js'
import { parisOffsetMinutes } from './halloween.js'

const MINUTE = 60000
const DAY = 24 * 60 * MINUTE
const TIMEZONE = 'Europe/Paris'

function parisWallTime(ms) {
  return ms + parisOffsetMinutes(ms) * MINUTE
}

export function lastHoursStart(closesAt) {
  const lastDay = Math.floor(parisWallTime(closesAt - 1) / DAY) * DAY
  const midnight = lastDay - DAY
  return midnight - parisOffsetMinutes(midnight) * MINUTE
}

export function operationPhase(operation, now) {
  if (!operation || !Number.isFinite(now)) return 'off'
  if (now < operation.announces_at) return 'off'
  if (now < operation.opens_at) return 'announcement'
  if (now >= operation.closes_at) return 'after'
  if (now >= lastHoursStart(operation.closes_at)) return 'last_hours'
  return 'open'
}

export function isProPlusVisitor({ loggedIn, user }) {
  return Boolean(loggedIn && user?.company?.verified_at)
}

export function visitorCode(operation, visitor) {
  const codes = operation?.codes
  if (!codes) return null
  return (isProPlusVisitor(visitor) ? codes.pro : codes.individual) || null
}

export function operationCountdown(target, now) {
  const total = Math.max(0, Math.floor((target - now) / 1000))
  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
    totalHours: Math.floor(total / 3600),
  }
}

const DAY_STYLES = {
  long: { weekday: 'long', day: 'numeric', month: 'short' },
  short: { weekday: 'short', day: 'numeric', month: 'short' },
  weekday: { weekday: 'long' },
  full: { weekday: 'long', day: 'numeric', month: 'long' },
}

export function twoDigits(value) {
  return String(value).padStart(2, '0')
}

export function operationDay(locale, at, style = 'long') {
  return new Intl.DateTimeFormat(intlLocale(locale), {
    ...(DAY_STYLES[style] || DAY_STYLES.long),
    timeZone: TIMEZONE,
  })
    .formatToParts(new Date(at))
    .map(({ type, value }) =>
      locale === 'fr' && type === 'day' && value === '1' ? '1er' : value
    )
    .join('')
}

export function operationClosingDay(locale, closesAt, style = 'long') {
  return operationDay(locale, closesAt - 1, style)
}

export function phpRound2(value) {
  if (!Number.isFinite(value) || value === 0) return value
  let integral = value >= 0 ? Math.floor(value * 100) : Math.ceil(value * 100)
  const next = value >= 0 ? integral + 1 : integral - 1
  if (next / 100 === value) integral = next
  const sign = integral < 0 || (integral === 0 && value < 0) ? -1 : 1
  if (Math.abs(value) >= Math.abs((integral + sign * 0.5) / 100)) {
    integral += sign
  }
  return integral / 100
}

function codeDiscount(price, value) {
  return price - price * (1 - value * 0.01)
}

export function priceWithCode(priceTtc, value) {
  return phpRound2(priceTtc - codeDiscount(priceTtc, value))
}

export function priceWithCodeHt(priceTtc, value, vatRate) {
  const rate = 1 + vatRate * 0.01
  return phpRound2(priceTtc / rate - codeDiscount(priceTtc, value) / rate)
}

export function isEligibleForCode(product, variant, offer) {
  if (!offer || !product || product.is_gift_card) return false
  if (
    offer.can_be_combined_with_discounts === false &&
    variant?.has_active_discount
  ) {
    return false
  }
  const brandId = product.brand_id ?? product.brand?.id
  return offer.brands.some(brand => brand.id === brandId)
}

const RUNNING_PHASES = ['open', 'last_hours']

export function operationRunning(operation, now) {
  return RUNNING_PHASES.includes(operationPhase(operation, now))
}

export function operationBrandList(locale, offer) {
  const names = (offer?.brands || []).map(brand => brand.name).filter(Boolean)
  if (typeof Intl.ListFormat !== 'function') return names.join(', ')
  return new Intl.ListFormat(intlLocale(locale), {
    style: 'long',
    type: 'conjunction',
  }).format(names)
}

export function operationPrice({
  operation,
  now,
  visitor,
  product,
  variant,
  beforeTax = false,
  vatRate = 20,
}) {
  if (!variant || !operationRunning(operation, now)) return null
  const offer = visitorCode(operation, visitor)
  if (!isEligibleForCode(product, variant, offer)) return null
  const priceTtc = variant.user_price_ttc
  return {
    code: offer.code,
    value: offer.value,
    current: beforeTax ? variant.user_price_ht : priceTtc,
    withCode: beforeTax
      ? priceWithCodeHt(priceTtc, offer.value, vatRate)
      : priceWithCode(priceTtc, offer.value),
  }
}

const OPERATION_VISIBLE_PHASES = ['announcement', ...RUNNING_PHASES]

export function operationBannerPhase(operation, now) {
  const phase = operationPhase(operation, now)
  return OPERATION_VISIBLE_PHASES.includes(phase) ? phase : null
}

export function operationMenuVisible(operation, now) {
  return OPERATION_VISIBLE_PHASES.includes(operationPhase(operation, now))
}

export function operationPath(slug, locale, defaultLocale) {
  return locale === defaultLocale ? `/${slug}` : `/${locale}/${slug}`
}

export function countdownTarget(operation, phase) {
  if (phase === 'announcement') return operation.opens_at
  if (phase === 'open' || phase === 'last_hours') return operation.closes_at
  return null
}

export function operationBadgeProductClickEvent({
  productId,
  code,
  language,
  eventId,
}) {
  return {
    event: 'operation_badge_product_click',
    event_id: eventId,
    product_id: productId,
    operation_code: code,
    ...(language ? { language } : {}),
  }
}

export function operationBannerClickEvent({ phase, language, eventId }) {
  return {
    event: 'operation_banner_click',
    event_id: eventId,
    operation_phase: phase,
    ...(language ? { language } : {}),
  }
}

function cartLineCovered(line, offer) {
  const variant = line?.variant
  if (!variant || line.offer_id) return false
  return isEligibleForCode(
    variant.product,
    {
      has_active_discount: Boolean(
        variant.active_discount_id ?? variant.active_discount
      ),
    },
    offer
  )
}

export function operationCartSaving(lines, offer) {
  const base = (lines || [])
    .filter(line => cartLineCovered(line, offer))
    .reduce((sum, line) => sum + Number(line.total_ttc || 0), 0)
  return phpRound2(codeDiscount(base, offer.value))
}

export function operationCartReminder({ offer, order }) {
  if (!offer || !order) return null
  if (order.code?.code?.toUpperCase() === offer.code.toUpperCase()) return null
  const saving = operationCartSaving(order.order_variants, offer)
  const appliedDiscount = Number(order.total_code_ttc || 0)
  const gain = phpRound2(saving - appliedDiscount)
  if (saving <= 0 || gain <= 0) return null
  return { code: offer.code, saving: gain, additional: appliedDiscount > 0 }
}

export function operationCodeApplyEvent({ code, source, language, eventId }) {
  return {
    event: 'operation_code_apply',
    event_id: eventId,
    operation_code: code,
    source,
    ...(language ? { language } : {}),
  }
}
